import React, { useCallback, useEffect, useState } from 'react';
import { classes, IconButton } from '@bloomberg/media-ui-core';
import { ExitThin, Play } from '@bloomberg/media-ui-icons';
import { BannerCloseReason, BannerDataAuthorSession } from 'src/types/index';

import styles from './AuthorSession.module.css';

export type AuthorSessionProps = BannerDataAuthorSession & {
  onClose?: (reason: BannerCloseReason) => void;
  title: string;
  url?: string;
  imageUrl?: string;
  status: string;
  desktopBreakpointClassName?: string;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
};

export const AuthorSession = React.forwardRef<
  HTMLDivElement,
  AuthorSessionProps
>(function AuthorSessionComponent(props: AuthorSessionProps, ref) {
  const {
    imageUrl,
    status,
    title,
    url = '',
    onClose = () => {},
    desktopBreakpointClassName,
    trackingFunction = () => {},
  } = props;

  const [closed, setClosed] = useState(false);

  const cx = classes([styles.authorSessionContainer, closed && styles.closed]);

  useEffect(() => {
    trackingFunction('impression', `authorSession_${title}`);
  }, []);

  const onClick = useCallback(() => {
    trackingFunction('click', `authorSession_${title}`);
  }, [title]);

  const handleExit = useCallback((reason: BannerCloseReason) => {
    return function handler(_: React.MouseEvent<HTMLButtonElement>) {
      onClose(reason);
      trackingFunction('click', 'authorSession_close');
      setClosed(true);
    };
  }, []);

  if (!title) return null;

  return (
    <div className={cx} ref={ref}>
      <div className={classes([styles.content, desktopBreakpointClassName])}>
        {imageUrl && (
          <div className={styles.imageContainer}>
            <a href={url} tabIndex={-1} onClick={onClick}>
              <img className={styles.image} src={imageUrl} alt={title} />
            </a>
          </div>
        )}
        <div className={styles.textContainer}>
          <a
            className={styles.statusLink}
            href={url}
            tabIndex={-1}
            onClick={onClick}
          >
            {status}
          </a>
          <a href={url} onClick={onClick}>
            <span className={styles.title}>{title}</span>
          </a>
          <a className={styles.joinNowLink} href={url} onClick={onClick}>
            <div className={styles.joinNow}>
              <span className={styles.playButton}>
                <Play />
              </span>
              <span className={styles.joinNowText}>Join Now</span>
            </div>
          </a>
        </div>
        <IconButton
          onClick={handleExit('ExitClick')}
          className={styles.iconButton}
        >
          <ExitThin />
        </IconButton>
      </div>
    </div>
  );
});
